import type { FollowChannelManagement, ManagementPolicy } from "@app/shared/policy";
import { clearToken, getToken, setToken } from "./auth";
import { SELF_TRADER_VENUE } from "./selfTrader";

/**
 * The only place in this app that talks to the server.
 *
 * Two rules keep the rest of the app honest: a non-2xx response always throws a typed
 * `ApiError` carrying the server's own `{error, message}`, and a 401 always clears the
 * token and routes to `/login` — "logged out" and "no data" must never render the same.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** The rest of the error body beyond `{error, message}` — e.g. `/api/trade`'s `candidates`
   *  and `failures` arrays, which a screen may need to render rather than just report. */
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let onUnauthorized: (() => void) | null = null;

/** Wired once from the router so api.ts never imports react-router itself. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });

  if (res.status === 401 && path !== "/api/auth/login") {
    clearToken();
    onUnauthorized?.();
  }

  if (!res.ok) {
    let error = "request_error";
    let message = `${res.status} ${res.statusText}`;
    let details: Record<string, unknown> = {};
    try {
      const body = (await res.json()) as { error?: string; message?: string } & Record<string, unknown>;
      if (body.error) error = body.error;
      if (body.message) message = body.message;
      details = body;
    } catch {
      /* non-JSON error body; the status line is all we have */
    }
    throw new ApiError(res.status, error, message, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Wire types. Hand-written rather than reused from @app/db: numeric columns cross the
// network as strings and timestamps as ISO strings, neither of which match drizzle's own
// inferred row types (Date, and driver-dependent numeric handling).
// ---------------------------------------------------------------------------

export type HealthCheck = { state: "ok" | "degraded" | "down" | "disabled"; detail?: string; meta?: unknown };
export type Health = {
  state: "ok" | "degraded" | "down";
  version: string;
  sha: string;
  startedAt: string;
  uptimeSeconds: number;
  modules: Record<string, boolean>;
  checks: Record<string, HealthCheck>;
};

export type SignalStatus =
  | "parsed"
  | "skipped"
  | "awaiting_approval"
  | "executing"
  | "live"
  | "closed"
  | "cancelled"
  | "error";

export type SignalListItem = {
  id: number;
  channelId: number;
  channelTitle: string;
  messageId: number;
  status: SignalStatus;
  symbol: string | null;
  symbolRaw: string;
  brokerSymbol: string | null;
  direction: "buy" | "sell" | null;
  directionCorrected: boolean;
  entryType: "market" | "limit" | "stop" | null;
  entryPrice: string | null;
  stopLoss: string | null;
  takeProfits: string[] | null;
  confidence: number | null;
  skipReason: string | null;
  pipsRealized: string | null;
  profitMoney: string | null;
  closedAt: string | null;
  createdAt: string;
};

export type Order = {
  id: number;
  signalId: number | null;
  source: "signal" | "manual_app" | "external" | "agent";
  slot: number | null;
  clientId: string | null;
  metaapiOrderId: string | null;
  positionId: string | null;
  state:
    | "planned"
    | "submitted"
    | "pending"
    | "open"
    | "closed"
    | "cancelled"
    | "failed"
    | "simulated_open"
    | "simulated_closed"
    /** Sent, but the broker never confirmed whether it became a position. Needs a human. */
    | "in_doubt";
  symbol: string;
  brokerSymbol: string;
  direction: "buy" | "sell";
  volume: string;
  /** Null on rows written before M17; a resting order always has one. */
  entryType: "market" | "limit" | "stop" | null;
  requestedPrice: string | null;
  /** What the broker was told a resting order should expire at, if anything. */
  expiresAt: string | null;
  sl: string | null;
  tp: string | null;
  actualOpenPrice: string | null;
  actualClosePrice: string | null;
  /** The pip size this order is settled with (numeric string); null before its spec was known. */
  pipSize: string | null;
  closeReason: "tp" | "sl" | "manual" | "app" | "rule" | "signal_close" | "stopout" | "unknown" | null;
  closeReasonSource: "deal_reason" | "comment" | "price_match" | "inferred" | null;
  profit: string | null;
  commission: string | null;
  swap: string | null;
  openedAt: string | null;
  closedAt: string | null;
  lastError: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  /**
   * M27a — the owning signal's verdict, joined in by GET /api/orders so grouping trades by
   * signal needs no second round trip. Null means "this order answers to no signal" (manual
   * or external) — a fact the client branches on, distinct from a signal with missing data.
   */
  signal: {
    id: number;
    status: string;
    ourResult: "tp1" | "tp2" | "tp3" | "sl" | "be" | "none" | null;
    pipsRealized: string | null;
    profitMoney: string | null;
    channelTitle: string | null;
  } | null;
};

export type Deal = {
  id: string;
  entryType: "DEAL_ENTRY_IN" | "DEAL_ENTRY_OUT" | "DEAL_ENTRY_INOUT" | "DEAL_ENTRY_OUT_BY";
  orderId: number | null;
  positionId: string | null;
  clientId: string | null;
  type: string;
  symbol: string | null;
  volume: string | null;
  price: string | null;
  profit: string | null;
  commission: string | null;
  swap: string | null;
  reason: string | null;
  brokerComment: string | null;
  dealTime: string;
};

export type Decision = {
  id: number;
  kind: "parse" | "decide" | "resolve" | "rule_eval";
  channelId: number | null;
  messageId: number | null;
  signalId: number | null;
  eventId: number | null;
  model: string | null;
  promptVersion: string | null;
  input: Record<string, unknown>;
  output: Record<string, unknown> | null;
  actions: unknown[] | null;
  confidence: number | null;
  outcome: "executed" | "dry_run" | "skipped" | "escalated" | "error";
  guardFailures: string[] | null;
  latencyMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: string | null;
  error: string | null;
  createdAt: string;
};

export type FollowUpEvent = {
  id: number;
  channelId: number;
  messageId: number;
  type:
    | "modify_sl"
    | "modify_tp"
    | "close"
    | "partial_close"
    | "cancel"
    | "result_claim"
    | "ambiguous"
    // Not a follow-up: the channel edited the message behind a signal we had already traded.
    | "edited";
  signalId: number | null;
  status: "pending" | "resolving" | "escalated" | "applied" | "skipped" | "error";
  resolvedBy:
    | "reply_link"
    | "sole_candidate"
    | "symbol_match"
    | "direction_match"
    | "llm"
    | "user"
    | "timeout_policy"
    | null;
  payload: Record<string, unknown>;
  resolution: Record<string, unknown> | null;
  skipReason: string | null;
  escalatedAt: string | null;
  escalationDeadline: string | null;
  appliedAt: string | null;
  createdAt: string;
};

export type TelegramMessage = {
  id: number;
  channelId: number;
  tgMessageId: number;
  text: string | null;
  replyToTgMessageId: number | null;
  hasMedia: boolean;
  mediaPaths: string[] | null;
  postedAt: string;
  editedAt: string | null;
  editHistory: unknown[];
  deletedAt: string | null;
};

/**
 * M37 — the History tab's MT4-style total, computed server-side over the whole filtered
 * window rather than the (capped) page of rows. `net` includes commission and swap; every
 * figure is null when no order in the window carried money at all, which must render as a
 * dash rather than a zero.
 */
export type OrderTotals = {
  net: number | null;
  gross: number | null;
  commission: number | null;
  swap: number | null;
  count: number;
};

export type OrdersResponse = { orders: Order[]; totals: OrderTotals };

export type SignalDetail = {
  signal: SignalListItem & Record<string, unknown>;
  channel: { id: number; title: string; autoExecute: boolean };
  message: TelegramMessage;
  orders: Order[];
  deals: Deal[];
  decisions: Decision[];
  followUps: FollowUpEvent[];
};

/** M14 — a MetaApi account row plus its channel subscriptions. */
/** M15 — live credit standing per provider; null means honestly unknown, not zero. */
export type CreditsOverview = {
  metaapi: { remainingUsd: number | null };
  llm: {
    provider: "openai" | "openrouter";
    remainingUsd: number | null;
    /** What the figure above actually is, which decides how it must be labelled. */
    source: LlmCreditSource;
    spentTodayUsd: number;
    /** True means the balance is a routing-fee pot, not the pipeline's runway. */
    byok: boolean | null;
    baseline: { amountUsd: number | null; asOf: string | null };
  };
  thresholds: {
    metaapiWarnUsd: number;
    metaapiAlarmUsd: number;
    llmWarnUsd: number;
    llmAlarmUsd: number;
  };
  levels: { metaapi: CreditLevel | null; llm: CreditLevel | null };
};

export type LlmCreditSource = "credits-api" | "key-limit" | "metered-baseline" | "unknown";

export type CreditLevel = "ok" | "warn" | "alarm";

export type CreditHistoryPoint = {
  day: string;
  metaapiRemainingUsd: number | null;
  metaapiSpentUsd: number | null;
  llmRemainingUsd: number | null;
  llmSpentUsd: number | null;
};

/** M15 — a broadcast channel from the Telegram dialog list, offered by the picker. */
export type ChannelDialog = {
  tgChannelId: number;
  title: string;
  username: string | null;
  /** data-uri jpeg preview; null when the channel has no photo. */
  thumb: string | null;
};

export type MetaapiAccount = {
  id: number;
  metaapiId: string;
  name: string;
  server: string;
  platform: string;
  state: string;
  connectionStatus: string;
  syncedAt: string | null;
  label: string | null;
  tradesChannelId: number | null;
  isActive: boolean;
  /** M22 — the account channels are RATED on. Mutually exclusive with `isActive`. */
  isSimBase: boolean;
  archivedAt: string | null;
  createdAt: string;
  subscribedChannelIds: number[];
};

export type Channel = {
  id: number;
  tgChannelId: number;
  title: string;
  role: "signal" | "system" | "trades";
  enabled: boolean;
  autoExecute: boolean;
  symbolHints: { aliases: Record<string, string>; notes: string };
  lotPolicyOverride: unknown;
  slTpPaddingPips: unknown;
  lastSeenTgMessageId: number;
  /** data-uri jpeg fetched from Telegram at enlist time (or boot backfill); null = none. */
  avatar: string | null;
  avatarFetchedAt: string | null;
  /**
   * M12d. Null is a real answer, not a missing one: below five signals a channel has no
   * score at all, because a brand-new channel scoring 100 would read as "verified
   * trustworthy" when it means "nothing has happened yet". Renders as a dash.
   */
  reliabilityScore: string | null;
  shadyLevel: number | null;
  reliabilityInputs: Record<string, number | null> | null;
  reliabilityComputedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
};

/** One row of `stats_daily`, or a fold of several. Nulls mean "nothing", never zero. */
export type DailyStats = {
  day: string;
  channelId: number | null;
  channelTitle: string | null;
  signalsClosed: number;
  wins: number;
  losses: number;
  breakeven: number;
  /** Closes this system could not classify. Reported, never folded into losses. */
  unclassified: number;
  tp1Hits: number;
  tp2Hits: number;
  tp3Hits: number;
  slHits: number;
  pipsClaimed: number | null;
  pipsRealized: number | null;
  profitMoney: number | null;
  foulCount: number;
};

export type FoulPlayEvent = {
  id: number;
  type: "deleted_signal" | "false_tp_claim" | "edited_levels_after_fill" | "sl_before_claimed_tp";
  channelId: number | null;
  signalId: number | null;
  messageId: number | null;
  dedupeKey: string;
  evidence: Record<string, unknown>;
  detectedAt: string;
  createdAt: string;
};

export type ChannelStatsResponse = {
  channel: {
    id: number;
    title: string;
    /** Already a number here — the server converts the `numeric` column at the boundary. */
    reliabilityScore: number | null;
    shadyLevel: number | null;
    reliabilityInputs: Record<string, number | null> | null;
    reliabilityComputedAt: string | null;
  };
  from: string;
  to: string;
  totalDays: number;
  currency: string;
  /** Today, which the nightly rollup has not covered yet, so it is computed live. */
  provisionalDay: string;
  daysWithActivity: number;
  totals: DailyStats;
  series: DailyStats[];
  /** M28b — cumulative realized pips over the window's closes, the sequence drawdown walks. */
  curve: { t: string; cumPips: number }[];
  foulPlay: { events: FoulPlayEvent[]; nextCursor: number | null };
};

/**
 * M22 — one channel's row in the comparison table.
 *
 * Everything is in PIPS. `profit_money` is null for a simulated close by design (there was
 * no deal), so a money-based profit factor would be blank in exactly the case this table
 * exists to serve: a channel being rated on paper before it is trusted with real money.
 *
 * Every `| null` here means "unknown", never zero, and the table renders each of them as a
 * dash. A `profitFactor` of null is a channel that has not lost yet — which is not an
 * infinite edge, it is no evidence.
 */
export type ChannelRating = {
  channelId: number;
  channelTitle: string;
  /** What is happening to this channel right now, so an empty row can explain itself. */
  measurement: "live" | "rated" | "off";
  signalsClosed: number;
  wins: number;
  losses: number;
  breakeven: number;
  winRate: number | null;
  pipsRealized: number | null;
  pipsClaimed: number | null;
  profitFactor: number | null;
  avgWinPips: number | null;
  avgLossPips: number | null;
  expectancyPips: number | null;
  /** Deepest peak-to-trough of the cumulative pips curve, reported positive. */
  maxDrawdownPips: number | null;
  /** Signed run of classified results at the window's end; be/none neither extend nor break. */
  currentStreak: number;
  longestWinStreak: number;
  longestLossStreak: number;
  avgHoldMinutes: number | null;
  foulCount: number;
  /** Claimed minus realized. Positive means their scoreboard flatters them. */
  pipsOverclaimed: number | null;
  reliabilityScore: number | null;
  shadyLevel: number | null;
};

export type ChannelComparisonResponse = {
  from: string;
  to: string;
  totalDays: number;
  channels: ChannelRating[];
};

/**
 * M23 — one row of the outbox, as the app reads it.
 *
 * `plain` is the server's text rendering of the Telegram HTML in `body`; the client needs no
 * HTML stripper of its own, and the two can never disagree about what a message says.
 *
 * `status` is deliberately independent of whether this is readable. A notification Telegram
 * never sent is exactly the one worth reading here.
 */
export type AppNotification = {
  id: number;
  kind: string;
  target: "system" | "trades";
  body: string;
  plain: string;
  status: "pending" | "sent" | "failed" | "suppressed";
  lastError: string | null;
  dedupeKey: string | null;
  createdAt: string;
  sentAt: string | null;
  readAt: string | null;
};

export type PushDevice = {
  id: number;
  userAgent: string | null;
  lastSeenAt: string;
  createdAt: string;
};

export type PushConfigResponse = {
  /** False when the box has no VAPID keys. A state to render, not an error. */
  configured: boolean;
  publicKey: string | null;
  devices: PushDevice[];
};

export type NotificationsResponse = {
  notifications: AppNotification[];
  nextCursor: number | null;
  /** Over everything, not over the current filter — this is the badge's number. */
  unreadCount: number;
};

export type StatsOverviewResponse = {
  period: "daily" | "weekly" | "monthly";
  from: string;
  to: string;
  totalDays: number;
  currency: string;
  rows: DailyStats[];
  daysRolledUp: number;
  provisionalDay: string | null;
};

/**
 * `/api/reports` — a *calendar* period, deliberately not the trailing window
 * `/api/stats/overview` answers. "How are we doing lately" and "what did Monday's report say"
 * are different questions and the server keeps them apart; so does this type.
 */
export type ReportResponse = StatsOverviewResponse & {
  /** Whole periods back. 0 is the period the most recent posted report covered. */
  offset: number;
};

export type BrokerPosition = {
  id: string;
  symbol?: string;
  type?: string;
  volume?: number;
  openPrice?: number;
  currentPrice?: number;
  stopLoss?: number;
  takeProfit?: number;
  profit?: number;
  swap?: number;
  commission?: number;
  clientId?: string;
  time?: string;
  order: Order | null;
  origin: "signal" | "manual_app" | "external" | "agent";
  signalId: number | null;
  slot: number | null;
};

export type PositionsResponse = { live: true; positions: BrokerPosition[] } | { live: false; positions: []; reason: string };
export type AccountResponse =
  | { live: true; account: Record<string, unknown> }
  | { live: false; account: null; reason: string };

export type SettingRow = { key: string; value: unknown; valid: boolean; stored: boolean; default: unknown; description: string };

export type ManualTradeInput = {
  symbol: string;
  direction: "buy" | "sell";
  /** Null means market. */
  entryPrice: number | null;
  sl: number;
  tp: number;
  /** M17 — optional server-side trailing stop, in pips; the server converts to price. */
  trailingPips?: number;
  /** M24b — a lot size for this trade only. Absent means the global lot policy. */
  volume?: number;
};

/**
 * M24c — why a stated entry price is going to market anyway. Null when market was asked for,
 * and null when the pending order will genuinely rest.
 */
export type MarketConversion =
  | { reason: "near_market"; distancePips: number; thresholdPips: number }
  | { reason: "no_quote" };

export type TradePreviewResponse = {
  ok: true;
  brokerSymbol: string;
  entryType: "market" | "limit" | "stop";
  marketConversion: MarketConversion | null;
  requestedPrice: number | null;
  /** The size actually used — the override when one was typed, otherwise the policy's. */
  volume: number | null;
  /** M24b — what the global lot policy would have sized, for showing beside an override. */
  policyVolume: number | null;
  volumeSource: "policy" | "override";
  stopPips: number | null;
  moneyAtRisk: number | null;
  tpPips: number | null;
  moneyAtTp: number | null;
  trailing: { distancePips: number; distancePrice: number } | null;
  lotNotes: string[];
  allowed: boolean;
  mode: "live" | "dry_run" | null;
  failures: string[];
  warnings: string[];
};

export type TradeSubmitResponse = { ok: true; orderId: number; queued: "open_manual"; replay: boolean };

/** M17 — one row of GET /api/symbols: a broker instrument with its pip metadata. */
export type BrokerSymbol = {
  symbol: string;
  digits: number | null;
  pipSize: number | null;
  point: number | null;
  minVolume: number | null;
  maxVolume: number | null;
  volumeStep: number | null;
  stopsLevel: number | null;
  profitCurrency: string | null;
};

export type SymbolsResponse = { symbols: BrokerSymbol[]; live: boolean; reason?: string };

// Mirrors packages/shared/src/schemas/rules.ts's zod schemas by hand, same reasoning as
// everywhere else in this file: this is what actually crosses the wire, not what Drizzle
// or zod infer server-side.
export type RuleTrigger = "ENTRY_FILLED" | "TP1_HIT" | "TP2_HIT" | "TP3_HIT" | "SL_HIT" | "SIGNAL_CLOSED";

export type RuleAction =
  | { type: "MOVE_SL_TO_BE"; offsetPips: number; bufferSpreads: number; slots?: number[] }
  | { type: "MOVE_SL_TO_TP"; tpSlot: number; slots?: number[] }
  | { type: "MOVE_SL_TO_R"; rMultiple: number; slots?: number[] }
  | { type: "CLOSE_REMAINING"; slots?: number[] }
  | { type: "PARTIAL_CLOSE"; pct: number; slots?: number[] }
  | { type: "SET_TRAILING"; distancePips: number; slots?: number[] }
  | { type: "CANCEL_PENDING"; slots?: number[] };

export type Rule = { trigger: RuleTrigger; actions: RuleAction[] };
export type RuleSet = Rule[];

export type RuleConfigRow = {
  id: number;
  scope: "global" | "channel";
  channelId: number | null;
  enabled: boolean;
  rules: RuleSet;
  /** The preset these rules were compiled from. Null IS the answer: it means Advanced mode. */
  policy: ManagementPolicy | null;
  followChannelManagement: FollowChannelManagement;
  updatedAt: string;
  createdAt: string;
};

export type ChannelRulesResponse = {
  /** True when the stored rules no longer match what the stored policy compiles to. Surfaced,
   *  never corrected — recompiling on a read would rewrite a hand-edited row silently. */
  policyDrift: boolean;
} & (
  | { scope: "channel"; config: RuleConfigRow; inherited?: undefined }
  | { scope: "global"; config: RuleConfigRow; inherited: true }
);

/**
 * What a save sends: a policy OR raw rules, never both. The server refuses a body carrying
 * both, and this union is that refusal expressed where the mistake would be made.
 */
export type RuleConfigWriteBody =
  | { policy: ManagementPolicy; enabled?: boolean; followChannelManagement?: FollowChannelManagement }
  | { rules: RuleSet; enabled?: boolean; followChannelManagement?: FollowChannelManagement };

export type LotPolicy =
  | { mode: "fixed"; lotsPerOrder: number }
  | { mode: "risk_pct"; riskPct: number; maxLots: number };

export type PriceSanity = { defaultPct: number; overrides: Record<string, number> };
/** W2.9: per symbol, because the threshold is in broker pips and a gold pip is not a EURUSD pip. */
export type MarketThreshold = { defaultPips: number; overrides: Record<string, number> };
export type ExposureCaps = { maxOpenSignals: number; maxTotalRiskPct: number };
export type WeekendSchedule = { enabled: boolean; undeployCron: string; redeployCron: string; tz: string };
export type ModelsSetting = { parser: string; decider: string; deciderEffort: "low" | "medium" | "high" };

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * M41 — the self-trader's wire types, copied from `@app/self-trader` (which is Node-only and
 * external to the web build). Times are epoch milliseconds, as the module keeps them.
 */
export type SelfTraderMode = "observe" | "demo" | "shadow";
export type SelfTraderStatus = {
  running: boolean;
  mode: SelfTraderMode;
  control: {
    paused: string | null;
    halt: string | null;
    peak: number;
    day: string;
    dayStart: number;
    baselineBalance: number | null;
    baselineAt: number;
    lastSnapshot: number | null;
    lastResearch: number;
    lastPromotion: number;
  };
  health: { state: "ok" | "degraded" | "stopped"; reasons: string[] };
  active: string[];
  unresolved: number;
};
export type PortfolioSummary = {
  strategyId: string;
  hash: string;
  symbol: string;
  /** 1440: a multi-day strategy (M49), daily bars, long only, held overnight. */
  timeframe: 15 | 60 | 1440;
  family: string;
  baseline: boolean;
  state: "simulation" | "eligible" | "active" | "retired";
  reasons: string[];
  capital: number;
  equity: number;
  net: number;
  maxDrawdown: number;
  halted: boolean;
  closedTrades: number;
  winRate: number | null;
  profitFactor: number | null;
  pips: number;
  open: { side: "buy" | "sell"; entry: number; stop: number; target: number; openedAt: number } | null;
  pending: { side: "buy" | "sell"; at: number; expiresAt: number } | null;
  forward: {
    /** False for a strategy the promoter never considers (an AI variant, risk sizing, or a multi-day one). */
    promotable: boolean;
    /** The promoter's net: every non-fixed service cost since the start deducted, not only this portfolio's own. */
    net: number;
    startedAt: number | null;
    days: number;
    observedDays: number;
    closes: number;
    last50Net: number;
    equityAboveCapital: boolean;
    costsComplete: boolean;
    dataComplete: boolean;
    fresh: boolean;
    researchEvidence: boolean;
  };
};
/** The promoter's thresholds, served by the route so the page never hardcodes them. */
export type ForwardGates = { days: number; observedDays: number; closes: number; freshMs: number };
export type PortfoliosResponse = { portfolios: PortfolioSummary[]; currency: string; gates: ForwardGates };

export const api = {
  health: () => request<Health>("/api/health"),

  /**
   * M41 — the self-trader. Every call answers 503 while the module is off or connecting.
   * M47 — the eToro instance, the only one since the MT5 one was switched off.
   */
  selfTrader: {
    status: () => request<SelfTraderStatus>(`/api/self-trader/status${SELF_TRADER_VENUE}`),
    portfolios: () => request<PortfoliosResponse>(`/api/self-trader/portfolios${SELF_TRADER_VENUE}`),
    pause: (reason: string) =>
      request<{ ok: true }>(`/api/self-trader/pause${SELF_TRADER_VENUE}`, { method: "POST", body: JSON.stringify({ reason }) }),
    resume: () => request<{ ok: true }>(`/api/self-trader/resume${SELF_TRADER_VENUE}`, { method: "POST", body: JSON.stringify({}) }),
    resetHalt: (reason: string) =>
      request<{ ok: true }>(`/api/self-trader/reset-halt${SELF_TRADER_VENUE}`, { method: "POST", body: JSON.stringify({ reason }) }),
    flatten: (reason: string) =>
      request<{ actions: string[] }>(`/api/self-trader/flatten${SELF_TRADER_VENUE}`, { method: "POST", body: JSON.stringify({ reason }) }),
  },

  login: (username: string, password: string) =>
    request<{ token: string; expiresIn: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }).then((res) => {
      setToken(res.token);
      return res;
    }),

  signals: (params: { status?: string; channelId?: number; cursor?: number; limit?: number; from?: string; to?: string } = {}) =>
    request<{ signals: SignalListItem[]; nextCursor: number | null }>(
      `/api/signals?${new URLSearchParams(toStringParams(params))}`,
    ),

  signal: (id: number) => request<SignalDetail>(`/api/signals/${id}`),

  approveSignal: (id: number) => request<{ ok: true; signalId: number }>(`/api/signals/${id}/approve`, { method: "POST" }),

  closeSignal: (id: number, slots?: number[]) =>
    request<{ ok: true }>(`/api/signals/${id}/close`, {
      method: "POST",
      body: JSON.stringify(slots ? { slots } : {}),
    }),

  cancelSignal: (id: number, slots?: number[]) =>
    request<{ ok: true }>(`/api/signals/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify(slots ? { slots } : {}),
    }),

  /** M41 — `account`: `live` is the active account, `paper` every other one (channel rating). */
  orders: (
    params: { state?: string; signalId?: number; limit?: number; from?: string; to?: string; account?: "live" | "paper" } = {},
  ) =>
    request<OrdersResponse>(`/api/orders?${new URLSearchParams(toStringParams(params))}`),

  positions: () => request<PositionsResponse>("/api/positions"),

  account: () => request<AccountResponse>("/api/account"),

  /**
   * M24e — `volume` closes only that many lots. Omitted closes the whole position, which is
   * what this always did. Deliberately omitted rather than sent as the position's full size:
   * the server owns "how much is left", and a number from a screen rendered a while ago
   * could be stale.
   */
  closePosition: (positionId: string, volume?: number) =>
    request<{ ok: true; queued?: "close" | "partial_close" }>(
      `/api/positions/${encodeURIComponent(positionId)}/close`,
      { method: "POST", body: JSON.stringify(volume === undefined ? {} : { volume }) },
    ),

  /** M26a — the account's denomination, one string; "" when no snapshot has ever been taken. */
  currency: () => request<{ currency: string }>("/api/currency"),
  equity: (days?: number) =>
    request<{
      currency: string;
      from: string;
      to: string;
      points: { t: string; balance: number | null; equity: number | null }[];
      peakEquity: number | null;
      maxDrawdownMoney: number | null;
      currentDrawdownFromPeak: number | null;
    }>(`/api/stats/equity${days ? `?days=${days}` : ""}`),

  /**
   * M24a — keyed by ORDER id, unlike close and movePositionSl above. A resting order has no
   * position yet, so there is no positionId to address it by.
   */
  cancelOrder: (orderId: number) =>
    request<{ ok: true; queued: "cancel"; orderId?: number; signalId?: number; slots?: number[] }>(
      `/api/orders/${orderId}/cancel`,
      { method: "POST" },
    ),

  movePositionSl: (positionId: string, sl: number) =>
    request<{ ok: true; queued: "move_sl"; signalId: number; slots: number[]; newStop: number; note: string }>(
      `/api/positions/${encodeURIComponent(positionId)}`,
      { method: "PATCH", body: JSON.stringify({ sl }) },
    ),

  /**
   * M24f — the same route. One level at a time: MetaApi's modify call sets both, and the
   * server refuses a request naming both rather than half-applying it.
   */
  movePositionTp: (positionId: string, tp: number) =>
    request<{ ok: true; queued: "move_tp"; orderId: number; newTarget: number; note: string }>(
      `/api/positions/${encodeURIComponent(positionId)}`,
      { method: "PATCH", body: JSON.stringify({ tp }) },
    ),

  decisions: (params: { signalId?: number; messageId?: number; channelId?: number; cursor?: number; limit?: number } = {}) =>
    request<{ decisions: Decision[]; nextCursor: number | null }>(
      `/api/decisions?${new URLSearchParams(toStringParams(params))}`,
    ),

  channels: () => request<{ channels: Channel[] }>("/api/channels"),

  accounts: () => request<{ stale: boolean; accounts: MetaapiAccount[] }>("/api/accounts"),

  /** M29 — a paper-only account row with no MetaApi behind it; costs nothing, can never trade. */
  createVirtualAccount: (label: string) =>
    request<{ account: MetaapiAccount }>("/api/accounts/virtual", {
      method: "POST",
      body: JSON.stringify({ label }),
    }),
  createAccount: (body: {
    name: string;
    login: string;
    password: string;
    server: string;
    platform: "mt4" | "mt5";
    magic?: number;
    region?: string;
  }) => request<{ account: MetaapiAccount }>("/api/accounts", { method: "POST", body: JSON.stringify(body) }),

  updateAccount: (id: number, body: { label?: string | null; tradesChannelId?: number | null }) =>
    request<{ account: MetaapiAccount }>(`/api/accounts/${id}`, { method: "PUT", body: JSON.stringify(body) }),

  setAccountSubscriptions: (id: number, channelIds: number[]) =>
    request<{ subscribedChannelIds: number[] }>(`/api/accounts/${id}/subscriptions`, {
      method: "PUT",
      body: JSON.stringify({ channelIds }),
    }),

  deployAccount: (id: number) => request<{ ok: true }>(`/api/accounts/${id}/deploy`, { method: "POST" }),

  undeployAccount: (id: number) => request<{ ok: true }>(`/api/accounts/${id}/undeploy`, { method: "POST" }),

  activateAccount: (id: number) =>
    request<{ ok: true; restarting: boolean; alreadyActive?: boolean }>(`/api/accounts/${id}/activate`, {
      method: "POST",
    }),

  setSimulationBase: (id: number) =>
    request<{ ok: true; alreadySimBase?: boolean }>(`/api/accounts/${id}/simulation-base`, {
      method: "POST",
    }),

  clearSimulationBase: (id: number) =>
    request<{ ok: true; alreadyCleared?: boolean }>(`/api/accounts/${id}/simulation-base`, {
      method: "DELETE",
    }),

  removeAccount: (id: number) => request<{ ok: true }>(`/api/accounts/${id}`, { method: "DELETE" }),

  telegramDialogs: () => request<{ dialogs: ChannelDialog[] }>("/api/telegram/dialogs"),

  credits: () => request<CreditsOverview>("/api/credits"),

  creditsHistory: (days = 30) =>
    request<{ days: CreditHistoryPoint[] }>(`/api/credits/history?days=${days}`),

  createChannel: (body: { tgChannelId: number; title?: string; role?: Channel["role"]; enabled?: boolean; autoExecute?: boolean }) =>
    request<{ channel: Channel }>("/api/channels", { method: "POST", body: JSON.stringify(body) }),

  patchChannel: (
    id: number,
    patch: Partial<
      Pick<Channel, "title" | "role" | "enabled" | "autoExecute" | "symbolHints" | "lotPolicyOverride" | "slTpPaddingPips">
    >,
  ) => request<{ channel: Channel }>(`/api/channels/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  channelRules: (id: number) => request<ChannelRulesResponse>(`/api/channels/${id}/rules`),

  channelStats: (id: number, params: { days?: number; foulLimit?: number; foulCursor?: number } = {}) =>
    request<ChannelStatsResponse>(`/api/channels/${id}/stats?${new URLSearchParams(toStringParams(params))}`),

  /** M28c — the risk-1%-compounding replay; `note` names the approximation out loud. */
  channelHypothetical: (id: number, days?: number) =>
    request<{
      currency: string;
      base: number;
      final: number;
      curve: { t: string; balance: number }[];
      pricedCount: number;
      unpriceableCount: number;
      note: string;
    }>(`/api/channels/${id}/hypothetical${days ? `?days=${days}` : ""}`),
  /** M28c — closed signals by symbol and by the UTC weekday the channel posted. */
  channelBreakdown: (id: number, days?: number) =>
    request<{
      bySymbol: { symbol: string; closed: number; wins: number; losses: number; pips: number }[];
      byDayOfWeek: { dayOfWeek: number; closed: number; wins: number; losses: number; pips: number }[];
    }>(`/api/channels/${id}/breakdown${days ? `?days=${days}` : ""}`),

  /** Every signal channel side by side, over a trailing window ending today (M22). */
  channelComparison: (days = 30) =>
    request<ChannelComparisonResponse>(`/api/stats/channels?days=${days}`),

  notifications: (params: { limit?: number; cursor?: number; kind?: string; unread?: boolean } = {}) =>
    request<NotificationsResponse>(
      `/api/notifications?${new URLSearchParams(toStringParams(params))}`,
    ),

  unreadNotifications: () =>
    request<{ unreadCount: number }>("/api/notifications/unread-count"),

  markNotificationRead: (id: number, read: boolean) =>
    request<{ notification: AppNotification; unreadCount: number }>(
      `/api/notifications/${id}/read`,
      { method: "POST", body: JSON.stringify({ read }) },
    ),

  markAllNotificationsRead: (before: string) =>
    request<{ marked: number; unreadCount: number }>("/api/notifications/read-all", {
      method: "POST",
      body: JSON.stringify({ before }),
    }),

  statsOverview: (period: "daily" | "weekly" | "monthly" = "daily") =>
    request<StatsOverviewResponse>(`/api/stats/overview?period=${period}`),

  report: (period: "daily" | "weekly" | "monthly", offset: number) =>
    request<ReportResponse>(`/api/reports?period=${period}&offset=${offset}`),

  putChannelRulesBody: (id: number, body: RuleConfigWriteBody) =>
    request<{ config: RuleConfigRow; policyDrift: boolean }>(`/api/channels/${id}/rules`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** The only way back to the global set. Creating a channel override used to be permanent. */
  deleteChannelRules: (id: number) =>
    request<{ deleted: boolean }>(`/api/channels/${id}/rules`, { method: "DELETE" }),

  putGlobalRulesBody: (body: RuleConfigWriteBody) =>
    request<{ config: RuleConfigRow; policyDrift: boolean }>("/api/rules", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  globalRules: () => request<{ config: RuleConfigRow; policyDrift: boolean }>("/api/rules"),

  settings: () => request<{ settings: SettingRow[] }>("/api/settings"),

  putSetting: (key: string, value: unknown) =>
    request<{ key: string; value: unknown }>(`/api/settings/${key}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  pushConfig: () => request<PushConfigResponse>("/api/push/config"),

  subscribePush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ id: number }>("/api/push/subscriptions", {
      method: "POST",
      body: JSON.stringify(subscription),
    }),

  unsubscribePush: (endpoint: string) =>
    request<{ removed: number }>("/api/push/subscriptions", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),

  notifyTest: () => request<{ ok: true; notificationId: number }>("/api/notify-test", { method: "POST" }),

  symbols: () => request<SymbolsResponse>("/api/symbols"),

  quotesWarm: (symbol: string) =>
    request<{ ok: true; live: boolean; warmed: boolean }>("/api/quotes/warm", {
      method: "POST",
      body: JSON.stringify({ symbol }),
    }),

  tradePreview: (body: ManualTradeInput) =>
    request<TradePreviewResponse>("/api/trade/preview", { method: "POST", body: JSON.stringify(body) }),

  trade: (body: ManualTradeInput & { requestId: string }) =>
    request<TradeSubmitResponse>("/api/trade", { method: "POST", body: JSON.stringify(body) }),
};

function toStringParams(params: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) out[key] = String(value);
  }
  return out;
}
